import { useEffect, useState } from 'react';

import { getCurrentLanguage, useTranslation } from '@modernloop/i18n';
import { getDateFnsLocale, getLocalTimezone } from '@modernloop/shared/datetime';
import { formatDistanceToNow, intervalToDuration, parseISO } from 'date-fns';
import { noop } from 'lodash';

import IsoTimestamp, { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import { formatToTimeZone } from 'src/utils/datetime/Format';

const useDurationLabel = (timestamp: IsoTimestamp | undefined) => {
  // Subscribe to the active language so the label recomputes immediately on a locale switch
  // (not just on the next timestamp change or polling tick).
  const { i18n } = useTranslation();
  const [durationLabel, setDurationLabel] = useState(
    timestamp ? formatDistanceToNow(parseISO(timestamp), { addSuffix: true, locale: getDateFnsLocale() }) : ''
  );

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect owns timer lifecycle after render and cleanup.
  useEffect(() => {
    if (!timestamp) {
      return noop;
    }

    setDurationLabel(formatDistanceToNow(parseISO(timestamp), { addSuffix: true, locale: getDateFnsLocale() }));

    const duration = intervalToDuration({ start: parseISO(timestamp), end: Date.now() });
    let timeoutMs = 60 * 1000;

    const { years = 0, months = 0, weeks = 0, days = 0 } = duration;

    const showDurationLabel = years === 0 && months === 0 && weeks === 0 && days <= 7;

    if (!showDurationLabel) {
      setDurationLabel(
        formatToTimeZone(assertIsoTimestamp(timestamp), years > 0 ? 'MMM d, yyyy' : 'MMM d', getLocalTimezone())
      );
      return noop;
    }

    if (duration.days && duration.days > 0) {
      timeoutMs *= 60 * 24; // 60 minutes * 24 hours
    } else if (duration.hours) {
      timeoutMs *= 60; // 60 minutes
    }

    const intervalHandle = setInterval(() => {
      setDurationLabel(formatDistanceToNow(parseISO(timestamp), { addSuffix: true, locale: getDateFnsLocale() }));
    }, timeoutMs);

    return () => {
      clearInterval(intervalHandle);
    };
  }, [timestamp, i18n.language]);

  // These abbreviations ("about " removal, "minutes"->"min", "just now") are English-specific
  // string surgery; applying them to localized output would be wrong. Only run them for English.
  if (!getCurrentLanguage().startsWith('en')) {
    return durationLabel;
  }

  return durationLabel
    .replace('about ', '')
    .replace('minutes', 'min')
    .replace('minute', 'min')
    .replace('less than a min ago', 'just now');
};

export default useDurationLabel;
